import { groupFollowUps } from "../attention/attention-groups.js";
import type { FollowUp } from "../attention/followups-api.js";
import type { TaskOccurrence } from "../tasks/tasks-api.js";
import type { LocationDashboardMetrics } from "./dashboard-api.js";

export const TODAY_LIST_LIMIT = 5;

export interface TodayChecks {
  done: number;
  // Completed or skipped today.
  finished: TaskOccurrence[];
  // Not open yet today.
  later: TaskOccurrence[];
  missed: number;
  // Missed and due now, the missed ones first.
  now: TaskOccurrence[];
  total: number;
}

const byDue = (a: TaskOccurrence, b: TaskOccurrence) =>
  new Date(a.due_at).getTime() - new Date(b.due_at).getTime();

// The server marks a check due on its next sweep, so one whose window has
// already opened counts as due now.
function isOpenNow(item: TaskOccurrence, now: Date): boolean {
  if (item.status === "due") return true;
  if (item.status !== "upcoming") return false;
  const opens = item.window_start_at ?? item.due_at;
  return new Date(opens).getTime() <= now.getTime();
}

// Cancelled checks do not count; skipped ones stay in the total but are not done.
export function summariseChecks(
  occurrences: readonly TaskOccurrence[],
  now = new Date(),
): TodayChecks {
  const counted = occurrences.filter((item) => item.status !== "cancelled");
  const missed = counted.filter((item) => item.status === "missed");
  return {
    done: counted.filter((item) => item.status === "completed").length,
    finished: counted
      .filter(
        (item) => item.status === "completed" || item.status === "skipped",
      )
      .sort(byDue),
    later: counted
      .filter((item) => item.status === "upcoming" && !isOpenNow(item, now))
      .sort(byDue),
    missed: missed.length,
    now: [
      ...missed.sort(byDue),
      ...counted.filter((item) => isOpenNow(item, now)).sort(byDue),
    ],
    total: counted.length,
  };
}

export function progressLabel(done: number, total: number): string {
  return `${done} of ${total}`;
}

export function progressPercent(done: number, total: number): number {
  if (total <= 0) return 0;
  return Math.min(100, Math.round((done / total) * 100));
}

export function staffHeadline(done: number, total: number): string {
  if (total === 0) return "No checks today";
  return `My checks today — ${progressLabel(done, total)} done`;
}

export function checksHeadline(done: number, total: number): string {
  if (total === 0) return "Today's checks";
  return `Today's checks — ${progressLabel(done, total)} done`;
}

export function unassignedWarning(count: number): string | null {
  if (count <= 0) return null;
  return count === 1
    ? "1 check has nobody assigned"
    : `${count} checks have nobody assigned`;
}

export interface AttentionPreview {
  items: FollowUp[];
  missed: number;
}

// Decisions first, then items worth a look; missed checks only as a count.
export function attentionPreview(
  items: readonly FollowUp[],
  limit = TODAY_LIST_LIMIT,
): AttentionPreview {
  const groups = groupFollowUps(items);
  const shown = groups
    .filter((group) => group.key !== "missed_checks")
    .flatMap((group) => group.items)
    .slice(0, limit);
  return {
    items: shown,
    missed:
      groups.find((group) => group.key === "missed_checks")?.items.length ?? 0,
  };
}

export interface MoreCount {
  href: "/attention" | "/records" | "/setup" | "/tasks" | "/team";
  label: string;
  value: string;
}

export function moreCounts(metrics: LocationDashboardMetrics): MoreCount[] {
  return [
    {
      href: "/tasks",
      label: "Due today",
      value: String(metrics.dueToday.total),
    },
    { href: "/tasks", label: "Overdue", value: String(metrics.overdue.total) },
    {
      href: "/records",
      label: "Outside the usual range",
      value: String(metrics.outOfRange.total),
    },
    {
      href: "/attention",
      label: "Needs attention",
      value: String(metrics.attention.total),
    },
    {
      href: "/team",
      label: "Training current",
      value: `${metrics.training.current}/${metrics.training.total}`,
    },
    {
      href: "/records",
      label: "Excluded staff",
      value: String(metrics.excludedStaff),
    },
    {
      href: "/setup",
      label: "Coming up",
      value: String(metrics.comingUp),
    },
    {
      href: "/records",
      label: "Evidence completeness, 30 days",
      value:
        typeof metrics.evidence.percentage === "number"
          ? `${metrics.evidence.percentage}%`
          : "—",
    },
  ];
}

interface LocationCounts {
  metrics: Pick<LocationDashboardMetrics, "attention" | "overdue">;
  name: string;
}

// Your locations: most missed checks first, then most needing attention,
// then name.
export function orderLocations<T extends LocationCounts>(
  locations: readonly T[],
): T[] {
  return [...locations].sort(
    (a, b) =>
      b.metrics.overdue.total - a.metrics.overdue.total ||
      b.metrics.attention.total - a.metrics.attention.total ||
      a.name.localeCompare(b.name),
  );
}
